import { Cursor } from 'loro-crdt';
import { createId } from '@paralleldrive/cuid2';
import { applySuggestionAction, summariseFeedback } from './suggestions-core';
import { b64, unb64 } from './base64';
/** HITL text-range suggestions over a Loro text container. Each suggestion is a proposed replacement anchored to `[start, end)` via stable Loro cursors; accepting applies the edit, rejecting records feedback. */
export class Suggestions {
    loroDoc;
    textId;
    listId;
    events;
    /** @param options - Document, text-container, suggestion-list, and event options. */
    constructor(options) {
        this.loroDoc = options.doc;
        this.textId = options.textContainer;
        this.listId =
            options.listContainer ?? `${options.textContainer}:suggestions`;
        this.events = options.events;
    }
    /**
     * Gets the shared document.
     *
     * @returns The shared Loro document.
     */
    doc() {
        return this.loroDoc;
    }
    /**
     * Proposes a replacement for a text range.
     *
     * @param input - Replacement text, rationale, author, and target range.
     * @returns The persisted pending suggestion with stable cursor anchors.
     * @throws If the range cannot be anchored to a stable cursor.
     */
    propose(input) {
        // before:suggestion:created — handlers may rewrite input or throw to cancel.
        const proposal = this.events
            ? this.events.emit('before:suggestion:created', { input }).input
            : input;
        const text = this.loroDoc.getText(this.textId);
        const original = text.toString().slice(proposal.start, proposal.end);
        const startCursor = text.getCursor(proposal.start, -1);
        const endCursor = text.getCursor(proposal.end, 1);
        if (!startCursor || !endCursor)
            throw new Error('cannot anchor suggestion');
        const suggestion = {
            id: createId(),
            container: this.textId,
            anchor: {
                start: b64(startCursor.encode()),
                end: b64(endCursor.encode()),
            },
            original,
            suggested: proposal.suggested,
            rationale: proposal.rationale,
            status: 'pending',
            author: proposal.author,
            createdAt: Date.now(),
        };
        this.rawList().push(suggestion);
        this.loroDoc.commit();
        this.events?.notify('suggestion:created', { suggestion });
        return suggestion;
    }
    /**
     * Accepts a suggestion and applies its replacement to the text container.
     *
     * @param id - Pending suggestion identifier.
     * @throws If the anchored range no longer resolves or the underlying text changed since proposal.
     */
    accept(id) {
        this.transition(id, { type: 'accept' }, (s) => {
            const startPos = this.loroDoc.getCursorPos(Cursor.decode(unb64(s.anchor.start)));
            const endPos = this.loroDoc.getCursorPos(Cursor.decode(unb64(s.anchor.end)));
            // Refuse to edit if the anchored range no longer resolves — falling back to
            // offset 0 would corrupt the document. Aborts the accept (stays pending).
            if (!startPos || !endPos) {
                throw new Error('cannot accept suggestion: anchored range no longer resolvable');
            }
            const text = this.loroDoc.getText(this.textId);
            const currentRange = text.toString().slice(startPos.offset, endPos.offset);
            if (currentRange !== s.original) {
                throw new Error('cannot accept suggestion: anchored text changed since proposal');
            }
            text.delete(startPos.offset, endPos.offset - startPos.offset);
            text.insert(startPos.offset, s.suggested);
        });
    }
    /**
     * Rejects a suggestion.
     *
     * @param id - Pending suggestion identifier.
     * @param feedback - Optional human feedback.
     */
    reject(id, feedback) {
        this.transition(id, { type: 'reject', feedback });
    }
    notifyTransition(next) {
        if (next.status === 'accepted') {
            this.events?.notify('suggestion:accepted', { suggestion: next });
        }
        else if (next.status === 'rejected') {
            this.events?.notify('suggestion:rejected', {
                suggestion: next,
                feedback: next.feedback,
            });
        }
    }
    /**
     * Lists suggestions.
     *
     * @returns All suggestions in insertion order.
     */
    list() {
        return this.rawList().toArray();
    }
    /**
     * Summarizes resolved suggestion feedback for the model.
     *
     * @returns A model-facing feedback summary, or an empty string.
     */
    feedbackSummary() {
        return summariseFeedback(this.list());
    }
    /** Loro container id of the suggestion list, for guarding it against a forged doc-frame write. */
    get containerId() {
        return this.listId;
    }
    /** Raw stored records, snapshotted for a forged-write authorization check on a fork. */
    snapshot() {
        return this.rawList().toArray();
    }
    rawList() {
        return this.loroDoc.getMovableList(this.listId);
    }
    transition(id, action, sideEffect) {
        const list = this.rawList();
        const items = list.toArray();
        const index = items.findIndex((s) => s.id === id);
        const current = items[index];
        if (!current)
            return;
        const next = applySuggestionAction(current, action);
        if (next === current)
            return;
        if (sideEffect && next.status === 'accepted')
            sideEffect(next);
        list.set(index, next);
        this.loroDoc.commit();
        this.notifyTransition(next);
    }
}
//# sourceMappingURL=suggestions.js.map